import React from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../../shared/ui/page-header/page-header.jsx';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';
import { getEnumOptionLabel } from '../../../shared/i18n/enum-labels.js';
import {
  SYSTEM_EVENT_REGULATORY_CATEGORIES,
  SYSTEM_EVENT_SEVERITIES,
  SYSTEM_EVENT_SOURCES,
  SYSTEM_EVENT_TYPES,
  formatSystemEventTypeLabel,
} from '../model/system-events.model.js';
import SystemEventDetailDrawer from './system-event-detail-drawer.jsx';
import SystemEventReferenceValue from './system-event-reference-value.jsx';

function EnumOptions({ values, t, namespace, getLabel = (value) => value }) {
  return values.map((value) => (
    <option key={value} value={value}>{getEnumOptionLabel(t, namespace, value, getLabel(value))}</option>
  ));
}

function SystemEventsPageView({
  summaryCards,
  rows,
  filters,
  nextCursor,
  isLoading,
  isPageLoading,
  error,
  success,
  detailEvent,
  isDetailOpen,
  isDetailLoading,
  detailError,
  supportBundle,
  isSupportLoading,
  updateFilter,
  applyFilters,
  resetFilters,
  refresh,
  loadNextPage,
  openDetail,
  closeDetail,
  createSupportReference,
}) {
  const { t } = useTranslation();

  return (
    <>
      <FloatingAlerts error={error} success={success} />
      <PageHeader title={t('systemEvents.title')} subtitle={t('systemEvents.subtitle')} />

      <div className="row">
        {summaryCards.map((card) => (
          <div key={card.key} className="col-xl col-md-4 col-sm-6">
            <div className="card">
              <div className="card-body p-3">
                <div className="d-flex align-items-center gap-3">
                  <span
                    className={`rounded-circle text-white d-inline-flex align-items-center justify-content-center flex-shrink-0 ${card.toneClass}`}
                    style={{ width: '42px', height: '42px' }}
                  >
                    <i className={`${card.iconClass} fs-5`} />
                  </span>
                  <div>
                    <div className="fs-5 fw-semibold lh-sm">{isLoading ? '...' : card.value}</div>
                    <div className="text-muted small">{t(card.label)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
            <div>
              <h5 className="mb-1">{t('systemEvents.filters')}</h5>
              <p className="text-muted mb-0">{t('systemEvents.filtersSubtitle')}</p>
            </div>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={refresh} disabled={isLoading}>
              <i className="ti ti-refresh me-1" />
              {t('systemEvents.refresh')}
            </button>
          </div>

          <div className="row g-3 align-items-end">
            <div className="col-lg-2 col-md-4">
              <label className="form-label" htmlFor="system-events-from">{t('systemEvents.from')}</label>
              <input
                id="system-events-from"
                type="datetime-local"
                className="form-control"
                value={filters.from}
                onChange={(event) => updateFilter('from', event.target.value)}
              />
            </div>
            <div className="col-lg-2 col-md-4">
              <label className="form-label" htmlFor="system-events-to">{t('systemEvents.to')}</label>
              <input
                id="system-events-to"
                type="datetime-local"
                className="form-control"
                value={filters.to}
                onChange={(event) => updateFilter('to', event.target.value)}
              />
            </div>
            <div className="col-lg-2 col-md-4">
              <label className="form-label" htmlFor="system-events-severity">{t('systemEvents.severity')}</label>
              <select
                id="system-events-severity"
                className="form-select"
                value={filters.severity}
                onChange={(event) => updateFilter('severity', event.target.value)}
              >
                <option value="">{t('systemEvents.anySeverity')}</option>
                <EnumOptions values={SYSTEM_EVENT_SEVERITIES} t={t} namespace="systemEventSeverity" />
              </select>
            </div>
            <div className="col-lg-2 col-md-4">
              <label className="form-label" htmlFor="system-events-source">{t('systemEvents.source')}</label>
              <select
                id="system-events-source"
                className="form-select"
                value={filters.source}
                onChange={(event) => updateFilter('source', event.target.value)}
              >
                <option value="">{t('systemEvents.anySource')}</option>
                <EnumOptions values={SYSTEM_EVENT_SOURCES} t={t} namespace="systemEventSource" />
              </select>
            </div>
            <div className="col-lg-2 col-md-4">
              <label className="form-label" htmlFor="system-events-category">{t('systemEvents.category')}</label>
              <select
                id="system-events-category"
                className="form-select"
                value={filters.regulatoryCategory}
                onChange={(event) => updateFilter('regulatoryCategory', event.target.value)}
              >
                <option value="">{t('systemEvents.anyCategory')}</option>
                <EnumOptions values={SYSTEM_EVENT_REGULATORY_CATEGORIES} t={t} namespace="systemEventCategory" />
              </select>
            </div>
            <div className="col-lg-2 col-md-4">
              <label className="form-label" htmlFor="system-events-type">{t('systemEvents.eventType')}</label>
              <select
                id="system-events-type"
                className="form-select"
                value={filters.eventType}
                onChange={(event) => updateFilter('eventType', event.target.value)}
              >
                <option value="">{t('systemEvents.anyEventType')}</option>
                <EnumOptions values={SYSTEM_EVENT_TYPES} t={t} namespace="systemEventType" getLabel={formatSystemEventTypeLabel} />
              </select>
            </div>
            <div className="col-lg-2 col-md-4">
              <label className="form-label" htmlFor="system-events-request">{t('systemEvents.requestId')}</label>
              <input
                id="system-events-request"
                type="text"
                className="form-control"
                value={filters.requestId}
                onChange={(event) => updateFilter('requestId', event.target.value)}
              />
            </div>
            <div className="col-lg-2 col-md-4">
              <label className="form-label" htmlFor="system-events-correlation">{t('systemEvents.correlationId')}</label>
              <input
                id="system-events-correlation"
                type="text"
                className="form-control"
                value={filters.correlationId}
                onChange={(event) => updateFilter('correlationId', event.target.value)}
              />
            </div>
            <div className="col-lg-3 col-md-4 d-flex gap-2">
              <button type="button" className="btn btn-primary" onClick={applyFilters} disabled={isLoading}>
                <i className="ti ti-filter me-1" />
                {t('systemEvents.applyFilters')}
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={resetFilters} disabled={isLoading}>
                {t('common.reset')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
            <div>
              <h5 className="mb-1">{t('systemEvents.eventJournal')}</h5>
              <p className="text-muted mb-0">{t('systemEvents.eventJournalSubtitle')}</p>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table search-table align-middle mb-0">
              <thead>
                <tr>
                  <th>{t('systemEvents.time')}</th>
                  <th>{t('systemEvents.severity')}</th>
                  <th>{t('systemEvents.source')}</th>
                  <th>{t('systemEvents.event')}</th>
                  <th>{t('systemEvents.actor')}</th>
                  <th>{t('systemEvents.subject')}</th>
                  <th>{t('systemEvents.requestCorrelation')}</th>
                  <th>{t('systemEvents.archiveStatus')}</th>
                  <th className="text-end">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={9} className="text-muted">{t('common.loading')}</td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-muted">{t('systemEvents.noEvents')}</td>
                  </tr>
                ) : rows.map((row) => (
                  <tr key={row.key}>
                    <td className="text-nowrap">{row.occurredAtLabel}</td>
                    <td><span className={`badge ${row.severityBadgeClass}`}>{row.severityLabel}</span></td>
                    <td>{row.sourceLabel}</td>
                    <td>
                      <div className="fw-semibold">{row.title}</div>
                      <div className="text-muted small">{row.eventTypeLabel}</div>
                    </td>
                    <td><SystemEventReferenceValue reference={row.actor} label={row.actorLabel} /></td>
                    <td><SystemEventReferenceValue reference={row.subject} label={row.subjectLabel} /></td>
                    <td className="font-monospace small text-break">{row.correlationLabel}</td>
                    <td><span className={`badge ${row.archiveBadgeClass}`}>{row.archiveLabel}</span></td>
                    <td className="text-end">
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openDetail(row)}>
                        {t('systemEvents.details')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {nextCursor ? (
            <div className="d-flex justify-content-center mt-3">
              <button type="button" className="btn btn-outline-primary btn-sm" onClick={loadNextPage} disabled={isPageLoading}>
                {isPageLoading ? t('systemEvents.loadingMore') : t('systemEvents.loadMore')}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <SystemEventDetailDrawer
        isOpen={isDetailOpen}
        event={detailEvent}
        isLoading={isDetailLoading}
        isSupportLoading={isSupportLoading}
        error={detailError}
        supportBundle={supportBundle}
        closeDetail={closeDetail}
        createSupportReference={createSupportReference}
      />
    </>
  );
}

export default SystemEventsPageView;
